import { Link } from '@/components/ui'
import { HighlightText } from '@/components/ui/table/DataTable/HighlightText'
import type { TableData } from '@/components/ui/table/DataTable/types'
import type { CellContext, ColumnDef } from '@/components/ui/table/types'
import { NUMBER_MASK } from '@/modules/projects/components/Workbench/accounts'
import { cn, formatDate, humanizeText, numberToCurrency } from '@/utils'
import {
  type ActivityListRow,
  descriptionWithSymbol,
  priceScaleNote,
} from './detail/activityRows'
import type { AccountLabel } from './detail/detailScope'
import type { RealizedRow, RowSource } from './types'

// biome-ignore lint/suspicious/noExplicitAny: cell helpers are reused across the row shapes.
type AnyCell = CellContext<any, unknown>

const searchQuery = (cell: AnyCell) => cell.table.options.meta?.searchQuery

export const sourceHref = (
  source: Pick<RowSource, 'statementFileUrl' | 'sourcePage'>,
): string =>
  source.sourcePage
    ? `${source.statementFileUrl}#page=${source.sourcePage}`
    : source.statementFileUrl

const formatQuantity = (value: number) =>
  value.toLocaleString('en-US', { maximumFractionDigits: 4 })

// Rows the totals leave out are drawn grey, as on the board.
const QUIET_TYPES = new Set<ActivityListRow['type']>([
  'paired_transfer',
  'unsettled_trade',
])

const listInk = (row: ActivityListRow) =>
  QUIET_TYPES.has(row.type) ? 'text-ink-subtle' : 'text-blue-7'

const listText = (row: ActivityListRow, className?: string) =>
  cn('block truncate', listInk(row), className)

const listDateCell = (cell: CellContext<ActivityListRow, unknown>) => (
  <span className={listText(cell.row.original)}>
    {formatDate(cell.getValue() as string)}
  </span>
)

// A row the statement printed no date for is placed by its statement; the cell says so.
const listTradeDateCell = (cell: CellContext<ActivityListRow, unknown>) => (
  <span className={listText(cell.row.original)}>
    {formatDate(cell.getValue() as string) || 'No date'}
  </span>
)

// An unsettled trade has no settle date yet; the board says so instead of leaving a dash.
const settlesText = (row: ActivityListRow) =>
  row.settlementDate || (row.type === 'unsettled_trade' ? 'Not yet' : '')

const listPlainCell = (cell: CellContext<ActivityListRow, unknown>) => (
  <span className={listText(cell.row.original)}>
    <HighlightText
      text={String(cell.getValue() ?? '')}
      query={searchQuery(cell)}
    />
  </span>
)

const PairedNote = ({ row }: { row: ActivityListRow }) => {
  const partner = row.pairing?.partner
  if (!partner || row.type !== 'paired_transfer') return null
  return (
    <span className="block text-xs text-ink-subtle">
      Paired with {partner.accountName}, {formatDate(partner.date)}{' '}
      <Link
        href={sourceHref(partner)}
        className="text-xs font-medium text-brand-deep"
        newTab
      >
        {partner.sourcePage ? `Page ${partner.sourcePage}` : 'Statement'}
      </Link>
    </span>
  )
}

const listDescriptionCell = (cell: CellContext<ActivityListRow, unknown>) => {
  const row = cell.row.original
  const text = descriptionWithSymbol(row)
  return (
    <span className="min-w-0">
      <span className={listText(row)} title={text}>
        <HighlightText text={text} query={searchQuery(cell)} />
      </span>
      <PairedNote row={row} />
      {row.statementLabel && (
        <span className="block text-xs text-ink-subtle">
          {row.statementLabel}
        </span>
      )}
    </span>
  )
}

const listQuantityCell = (cell: CellContext<ActivityListRow, unknown>) => {
  const value = cell.getValue() as number | null
  if (value == null) return null
  return (
    <span className={listText(cell.row.original, 'tabular-nums')}>
      {formatQuantity(value)}
    </span>
  )
}

const listPriceCell = (cell: CellContext<ActivityListRow, unknown>) => {
  const row = cell.row.original
  if (row.price == null) return null
  const scale = priceScaleNote(row)
  return (
    <span className={listText(row, 'tabular-nums')}>
      {row.price.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 6,
      })}
      {scale && <span className="ml-1 text-xs text-ink-subtle">{scale}</span>}
    </span>
  )
}

const listAmountCell = (cell: CellContext<ActivityListRow, unknown>) => {
  const value = cell.getValue() as number | null
  if (value == null) return null
  return (
    <span className={listText(cell.row.original, 'tabular-nums')}>
      {numberToCurrency(value)}
    </span>
  )
}

const listPageCell = (cell: AnyCell) => {
  const source = cell.row.original as RowSource
  if (!source.statementFileUrl) return null
  return (
    <Link
      href={sourceHref(source)}
      className="text-xs font-medium text-brand-deep"
      title={source.statementFileName}
      newTab
    >
      {source.sourcePage ? `Page ${source.sourcePage}` : 'Statement'}
    </Link>
  )
}

type AccountLabels = Map<string, AccountLabel>

// The board's column width: room for "Charles Schwab •• 8817" in full.
const ACCOUNT_COLUMN_WIDTH = 214

// A long institution name gives way; the last 4 always shows.
const AccountLabelText = ({
  label,
  fallback,
  className,
}: {
  label: AccountLabel | undefined
  fallback: string
  className: string
}) => (
  <span className={cn('flex min-w-0 gap-1', className)} title={fallback}>
    <span className="truncate">{label?.institution ?? fallback}</span>
    {label?.last4 && (
      <span className="shrink-0 whitespace-nowrap">
        {NUMBER_MASK} {label.last4}
      </span>
    )}
  </span>
)

const accountColumn = <Row extends TableData>(
  accessorKey: string,
  labelOf: (row: Row) => {
    label: AccountLabel | undefined
    fallback: string
    className: string
  },
): ColumnDef<Row, unknown> => ({
  accessorKey,
  header: 'Account',
  size: ACCOUNT_COLUMN_WIDTH,
  cell: (cell) => <AccountLabelText {...labelOf(cell.row.original)} />,
})

const pageColumn = <Row extends TableData>(): ColumnDef<Row, unknown> => ({
  accessorKey: 'sourcePage',
  // DataTable hides a column with an empty header, so a space keeps the page links in view.
  header: ' ',
  size: 80,
  enableSorting: false,
  cell: listPageCell,
})

interface ListColumnOptions {
  // With more than one account in view (labels given), the Account column takes the Settles column's place.
  accountLabels: AccountLabels | null
}

// The merged Activity list: securities rows and cash ledger rows under one column set.
export const activityListColumns = ({
  accountLabels,
}: ListColumnOptions): ColumnDef<ActivityListRow, unknown>[] => [
  { accessorKey: 'date', header: 'Date', size: 100, cell: listTradeDateCell },
  accountLabels
    ? accountColumn<ActivityListRow>('accountName', (row) => ({
        label: accountLabels.get(row.accountId),
        fallback: row.accountName,
        className: listInk(row),
      }))
    : {
        id: 'settlementDate',
        accessorFn: settlesText,
        header: 'Settles',
        size: 100,
        cell: listDateCell,
      },
  { accessorKey: 'typeLabel', header: 'Type', size: 130, cell: listPlainCell },
  {
    accessorKey: 'description',
    header: 'Description',
    size: 200,
    meta: { isFluid: true, wrap: true },
    cell: listDescriptionCell,
  },
  {
    accessorKey: 'quantity',
    header: 'Quantity',
    size: 90,
    cell: listQuantityCell,
  },
  { accessorKey: 'price', header: 'Price', size: 120, cell: listPriceCell },
  { accessorKey: 'amount', header: 'Amount', size: 130, cell: listAmountCell },
  pageColumn<ActivityListRow>(),
]

const realizedText = (cell: AnyCell) => (
  <span className="block truncate text-blue-7">
    <HighlightText
      text={String(cell.getValue() ?? '')}
      query={searchQuery(cell)}
    />
  </span>
)

const realizedDateCell = (cell: AnyCell) => (
  <span className="block truncate text-blue-7">
    {formatDate(cell.getValue() as string)}
  </span>
)

const realizedMoneyCell = (cell: AnyCell) => {
  const value = cell.getValue() as number | null
  if (value == null) return null
  return (
    <span className="block truncate tabular-nums text-blue-7">
      {numberToCurrency(value)}
    </span>
  )
}

const realizedQuantityCell = (cell: AnyCell) => {
  const value = cell.getValue() as number | null | undefined
  if (value == null) return null
  return (
    <span className="block truncate tabular-nums text-blue-7">
      {formatQuantity(value)}
    </span>
  )
}

const termCell = (cell: AnyCell) => (
  <span className="block truncate text-blue-7">
    {humanizeText(String(cell.getValue() ?? ''))}
  </span>
)

// Realized gains as a paged list, the same way as Activity.
export const realizedListColumns = ({
  accountLabels,
}: ListColumnOptions): ColumnDef<RealizedRow, unknown>[] => [
  {
    accessorKey: 'liquidationDate',
    header: 'Sale date',
    size: 100,
    cell: realizedDateCell,
  },
  ...(accountLabels
    ? [
        accountColumn<RealizedRow>('accountLabel', (row) => ({
          label: accountLabels.get(row.account),
          fallback: row.accountLabel,
          className: 'text-blue-7',
        })),
      ]
    : []),
  {
    accessorKey: 'term',
    header: 'Term',
    size: 70,
    cell: termCell,
    meta: { exportValue: (value) => humanizeText(String(value)) },
  },
  {
    accessorKey: 'description',
    header: 'Description',
    size: 180,
    meta: { isFluid: true },
    cell: realizedText,
  },
  {
    accessorKey: 'quantity',
    header: 'Quantity',
    size: 90,
    cell: realizedQuantityCell,
  },
  {
    accessorKey: 'saleAmount',
    header: 'Proceeds',
    size: 120,
    cell: realizedMoneyCell,
  },
  {
    accessorKey: 'costBasis',
    header: 'Cost basis',
    size: 120,
    cell: realizedMoneyCell,
  },
  {
    accessorKey: 'gainLoss',
    header: 'Gain or loss',
    size: 120,
    cell: realizedMoneyCell,
  },
  pageColumn<RealizedRow>(),
]
